import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useActor } from '@/app/auth';
import { useOrgLookups } from '@/app/lookups';
import { getCachedData, setCachedData, optimisticRemoveTaskFromCache, getDashboardCacheKey, type DashboardCachedData } from '@/app/data-cache';
import { PageHeader, Button, Card, CardHeader, ConfirmDialog, EmptyState, DashboardSkeleton, SelectInput, StatusBadge } from '@/components/ui';
import { CloneTaskModal, QuickCompleteTaskModal, TaskCard, TaskSeriesCard, groupTasksIntoItems, type TaskSeriesGroup } from '@/components/task-widgets';
import { Icon, type IconName } from '@/components/Icons';
import { formatDate, todayInZone } from '@/lib/format';
import { archiveMissedRoundsInSeries, deleteTask, deleteTasksBatch, listTasks, snoozeTaskForRushHour, taskCounts, isTaskOverdue } from '@/services/task-service';
import { ensureTodayRecurringOccurrences } from '@/services/template-service';
import { autoExpireMissedIntradayRounds } from '@/services/operations-hub-service';
import type { Task } from '@/domain/types';
import { getGateway } from '@/services/gateway';
import { useToast } from '@/app/toast';

const BOARD_COLUMNS: Array<{ status: Task['status']; label: string }> = [
  { status: 'scheduled', label: 'مجدولة' },
  { status: 'pending', label: 'قيد الانتظار' },
  { status: 'in_progress', label: 'جارية' },
  { status: 'completed', label: 'مكتملة مؤخرًا' },
];

export function DashboardPage() {
  const { snapshot, access, settings, can, refresh } = useAuth();
  const actor = useActor();
  const navigate = useNavigate();
  const lookups = useOrgLookups();
  const toast = useToast();
  const gateway = getGateway();
  const [branchId, setBranchId] = useState('');

  const cacheKey = getDashboardCacheKey(actor?.companyId || '', actor?.membershipId || '', branchId);
  const cached = getCachedData<DashboardCachedData>(cacheKey);

  const [tasks, setTasks] = useState<Task[]>(() => cached?.tasks ?? []);
  const [approvals, setApprovals] = useState<Task[]>(() => cached?.approvals ?? []);
  const [counts, setCounts] = useState<{
    assigned: number;
    created: number;
    overdue: number;
    pendingApproval: number;
    personal: number;
    completed: number;
  } | null>(() => cached?.counts ?? null);
  const [loading, setLoading] = useState<boolean>(() => !cached);
  const [error, setError] = useState<string | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [seriesToDelete, setSeriesToDelete] = useState<TaskSeriesGroup | null>(null);
  const [taskToClone, setTaskToClone] = useState<Task | null>(null);
  const [taskToQuickComplete, setTaskToQuickComplete] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState(false);

  const quickCompleteEnabled = settings?.operationalFeatures.quickCompleteEnabled !== false;
  const autoExpireHourlyRounds = settings?.operationalFeatures.autoExpireHourlyRounds !== false;
  const canCloneTask = Boolean(actor && (actor.isPlatformSupport || can('task.create')));
  const canDeleteTaskRow = useCallback(
    (task: Task) => {
      if (!actor) return false;
      if (actor.isPlatformSupport || can('task.delete')) return true;
      return Boolean(
        (actor.membershipId && task.createdByMembershipId === actor.membershipId) ||
          task.createdByUserId === actor.userId,
      );
    },
    [actor, can],
  );

  const load = useCallback(async (isSilent = false) => {
    if (!actor) return;
    const currentCached = getCachedData<DashboardCachedData>(cacheKey);
    if (!isSilent && !currentCached) {
      setLoading(true);
    }
    setError(null);
    try {
      // توليد المهام المتكررة يجري في الخلفية دون تعطيل ظهور اللوحة
      if (settings?.autoGenerateRecurring) {
        void ensureTodayRecurringOccurrences(gateway, actor.companyId, actor.userId).then((created) => {
          if (created > 0) {
            void load(true);
          }
        }).catch(() => {});
      }
      const today = todayInZone(settings?.timeZone ?? 'Asia/Riyadh');
      const base = {
        companyId: actor.companyId,
        actorMembershipId: actor.membershipId,
        isPlatformSupport: actor.isPlatformSupport,
        supervisedMembershipIds: access?.supervisedMembershipIds ?? [],
      };
      const [activeTasks, completedTasks, pendingApprovalRows, assignedTasks] = await Promise.all([
        listTasks(gateway, {
          ...base,
          view: 'board',
          filters: {
            status: ['scheduled', 'pending', 'in_progress'],
            branchId: branchId || null,
            pageSize: 200,
          },
        }),
        listTasks(gateway, {
          ...base,
          view: 'board',
          filters: {
            status: ['completed'],
            from: today,
            branchId: branchId || null,
            pageSize: 50,
          },
        }),
        gateway.table('tasks').list({
          filter: {
            companyId: { op: 'eq', value: actor.companyId },
            approvalState: { op: 'eq', value: 'pending' },
            deletedAt: { op: 'isNull' },
          },
          orderBy: [{ field: 'updatedAt', direction: 'desc' }],
          limit: 8,
        }),
        listTasks(gateway, {
          ...base,
          view: 'assigned',
          filters: {
            branchId: branchId || null,
            pageSize: 1,
          },
        }),
      ]);

      const nextCounts = await taskCounts(gateway, {
        companyId: actor.companyId,
        membershipId: actor.membershipId,
        isPlatformSupport: actor.isPlatformSupport,
        userId: actor.userId,
        supervisedMembershipIds: access?.supervisedMembershipIds ?? [],
        activeTasks: activeTasks.rows,
        assignedTasks,
        completedCount: completedTasks.total ?? completedTasks.rows.length,
      });
      const allTasks = [...activeTasks.rows, ...completedTasks.rows];
      setTasks((prev) => {
        if (
          prev.length === allTasks.length &&
          prev[0]?.id === allTasks[0]?.id &&
          prev[prev.length - 1]?.id === allTasks[allTasks.length - 1]?.id &&
          !prev.some((t, i) => t.id !== allTasks[i]?.id || t.status !== allTasks[i]?.status || t.updatedAt !== allTasks[i]?.updatedAt)
        ) {
          return prev;
        }
        return allTasks;
      });
      setCounts((prev) => {
        if (prev && JSON.stringify(prev) === JSON.stringify(nextCounts)) return prev;
        return nextCounts;
      });
      setApprovals((prev) => {
        if (prev.length === pendingApprovalRows.length && prev[0]?.id === pendingApprovalRows[0]?.id) return prev;
        return pendingApprovalRows;
      });
      setLoading(false);
      setCachedData(cacheKey, {
        tasks: allTasks,
        counts: nextCounts,
        approvals: pendingApprovalRows,
      });

      // إغلاق الجولات الساعية الفائتة يتم في الخلفية دون تعطيل الواجهة
      if (autoExpireHourlyRounds && activeTasks.rows.length > 0) {
        void autoExpireMissedIntradayRounds(gateway, actor, activeTasks.rows).then((expired) => {
          if (expired > 0) {
            toast.info(`تم إغلاق ${expired} جولة ساعية فائتة تلقائيًا لانتهاء وقت نافذتها.`);
          }
        }).catch(() => {});
      }
    } catch (cause) {
      if (!currentCached) {
        setError(cause instanceof Error ? cause.message : 'تعذّر تحميل لوحة المهام');
      }
    } finally {
      setLoading(false);
    }
  }, [access?.supervisedMembershipIds, actor, autoExpireHourlyRounds, branchId, cacheKey, gateway, settings?.autoGenerateRecurring, settings?.timeZone, toast]);

  useEffect(() => {
    if (typeof (window as any).__dismissTaskFlowIntro === 'function') {
      (window as any).__dismissTaskFlowIntro();
    }
    const existing = getCachedData<DashboardCachedData>(cacheKey);
    if (existing) {
      setTasks(existing.tasks);
      setCounts(existing.counts);
      setApprovals(existing.approvals);
      setLoading(false);
      // Revalidate in background without blocking
      void load(true);
    } else {
      void load(false);
    }
  }, [cacheKey, load]);

  const grouped = useMemo(() => {
    const map = new Map<Task['status'], Task[]>();
    for (const column of BOARD_COLUMNS) map.set(column.status, []);
    for (const task of tasks) {
      const current = map.get(task.status);
      if (current) current.push(task);
      else if (task.status === 'cancelled') map.get('completed')?.push(task);
    }
    return map;
  }, [tasks]);

  const timeZone = settings?.timeZone ?? 'Asia/Riyadh';

  const groupedColumnItems = useMemo(() => {
    const map = new Map<Task['status'], ReturnType<typeof groupTasksIntoItems>>();
    for (const column of BOARD_COLUMNS) {
      const rows = grouped.get(column.status) ?? [];
      map.set(column.status, groupTasksIntoItems(rows, timeZone));
    }
    return map;
  }, [grouped, timeZone]);

  const overdueItems = useMemo(() => {
    return groupTasksIntoItems(
      tasks.filter((task) => isTaskOverdue(task)),
      timeZone,
    ).slice(0, 6);
  }, [tasks, timeZone]);
  const firstName = actor?.isPlatformSupport ? 'دعم المنصة' : snapshot?.membershipProfile?.fullName?.split(' ')[0] ?? 'بك';
  const preserveCompletedCount = settings?.completedTaskRetention?.preserveCompletedCount ?? true;
  const archivedCompletedCount = settings?.completedTaskRetention?.archivedCompletedCount ?? 0;
  const totalCompletedCount =
    counts !== null
      ? counts.completed + (preserveCompletedCount ? archivedCompletedCount : 0)
      : null;

  const quickActions: Array<{ label: string; icon: IconName; onClick: () => void; show: boolean }> = [
    {
      label: 'مهمة جديدة',
      icon: 'plus',
      onClick: () => navigate('/tasks/new'),
      show: Boolean(actor),
    },
    {
      label: 'قوالب التكرار',
      icon: 'repeat',
      onClick: () => navigate('/templates'),
      show: Boolean(access?.permissions.includes('task.template.manage')),
    },
    {
      label: 'التقارير',
      icon: 'chart',
      onClick: () => navigate('/reports'),
      show: Boolean(access?.permissions.includes('report.view')),
    },
    {
      label: 'إضافة عضو',
      icon: 'users',
      onClick: () => navigate('/admin/users'),
      show: Boolean(access?.permissions.includes('membership.manage')),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={
          <span className="dashboard-greeting">
            مرحبًا، <bdi>{firstName}</bdi>
          </span>
        }
        subtitle={`${formatDate(new Date(), timeZone)} · ملخص سير العمل والمهام اليومية`}
        actions={
          <>
            {quickActions
              .filter((action) => action.show)
              .slice(0, 3)
              .map((action) => (
                <Button
                  key={action.label}
                  variant={action.label === 'مهمة جديدة' ? 'primary' : 'outline'}
                  icon={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
          </>
        }
      />

      {error ? (
        <div className="card">
          <div className="card-body">
            <p className="danger-text small">{error}</p>
            <Button onClick={() => void load()} icon="refresh">
              إعادة المحاولة
            </Button>
          </div>
        </div>
      ) : null}

      {loading && tasks.length === 0 ? (
        <DashboardSkeleton />
      ) : (
        <>
          <div className="stat-grid pe-stagger">
            <button type="button" className="stat" style={{ textAlign: 'start', cursor: 'pointer' }} onClick={() => navigate('/tasks/assigned')}>
              <span className="stat-label">المسندة إليّ</span>
              <span className="stat-value">{counts?.assigned ?? '—'}</span>
              <span className="tiny muted">مهام مفتوحة ومسندة إليك</span>
            </button>
            <div className="stat">
              <span className="stat-label">أنشأتها</span>
              <span className="stat-value">{counts?.created ?? '—'}</span>
              <span className="tiny muted">كل المهام التي أنشأتها</span>
            </div>
            <button type="button" className="stat" style={{ textAlign: 'start', cursor: 'pointer' }} onClick={() => navigate('/tasks/overdue')}>
              <span className="stat-label">متأخرة</span>
              <span className="stat-value danger-text">{counts?.overdue ?? '—'}</span>
              <span className="tiny muted">تجاوزت موعد الاستحقاق</span>
            </button>
            <div className="stat">
              <span className="stat-label">بانتظار الاعتماد</span>
              <span className="stat-value">{counts?.pendingApproval ?? '—'}</span>
              <span className="tiny muted">إنجازات تحتاج قرارًا</span>
            </div>
            <button
              type="button"
              className="stat"
              style={{ textAlign: 'start', cursor: 'pointer' }}
              onClick={() => navigate('/tasks/assigned')}
            >
              <span className="stat-label">المهام المكتملة</span>
              <span className="stat-value">{totalCompletedCount ?? '—'}</span>
              <span className="tiny muted">
                {preserveCompletedCount
                  ? archivedCompletedCount > 0
                    ? `محفوظ حتى بعد الحذف (شامل ${archivedCompletedCount} محذوفة)`
                    : 'يبقى محسوبًا حتى بعد حذف المهام المكتملة'
                  : 'المهام المكتملة الحالية فقط'}
              </span>
            </button>
          </div>
        </>
      )}

      {approvals.length > 0 ? (
        <Card>
          <CardHeader
            title={
              <span className="row">
                <Icon name="check-circle" size={18} /> بانتظار اعتمادك أو مراجعتك
              </span>
            }
            actions={
              <Link to="/tasks/assigned" className="btn btn-sm btn-ghost">
                عرض الكل
              </Link>
            }
          />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>المهمة</th>
                  <th>الأولوية</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {approvals.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <Link to={`/tasks/${task.id}`} className="strong">
                        {task.title}
                      </Link>
                    </td>
                    <td>{task.priority === 'urgent' ? 'عاجلة' : task.priority === 'high' ? 'عالية' : task.priority === 'low' ? 'منخفضة' : 'عادية'}</td>
                    <td>
                      <StatusBadge status={task.status} />
                    </td>
                    <td>
                      <Link to={`/tasks/${task.id}`} className="btn btn-sm btn-primary">
                        مراجعة
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title={
            <span className="row">
              <Icon name="board" size={18} /> لوحة المهام
            </span>
          }
          actions={
            <div className="row">
              <SelectInput value={branchId} onChange={(event) => setBranchId(event.target.value)} aria-label="تصفية بالفرع">
                <option value="">كل الفروع</option>
                {lookups.branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </SelectInput>
              <Button size="sm" icon="refresh" onClick={() => void load()}>
                تحديث
              </Button>
            </div>
          }
        />
        <div className="card-body">
          {!loading && tasks.length === 0 ? (
            <EmptyState
              icon="board"
              title="لا توجد مهام بعد"
              description="ابدأ بإنشاء مهمة، أو أنشئ قالب تكرار للمهام اليومية."
              action={
                <Button variant="primary" icon="plus" onClick={() => navigate('/tasks/new')}>
                  إنشاء مهمة
                </Button>
              }
            />
          ) : (
            <div className="board pe-stagger">
              {BOARD_COLUMNS.map((column) => {
                const rows = grouped.get(column.status) ?? [];
                const items = groupedColumnItems.get(column.status) ?? [];
                return (
                  <section key={column.status} className="board-column" aria-label={column.label}>
                    <div className="board-column-header">
                      <span>{column.label}</span>
                      <div className="row" style={{ gap: 4 }}>
                        <span className="badge badge-muted">{items.length}</span>
                        {rows.length > items.length ? (
                          <span className="tiny muted" title={`إجمالي الجولات والمهام: ${rows.length}`}>
                            ({rows.length} جولة)
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="board-column-body">
                      {items.slice(0, 30).map((item) =>
                        item.kind === 'series' ? (
                          <TaskSeriesCard
                            key={item.key}
                            group={item.group}
                            timeZone={timeZone}
                            features={settings?.operationalFeatures}
                            onQuickComplete={
                              quickCompleteEnabled
                                ? (task) => setTaskToQuickComplete(task)
                                : undefined
                            }
                            onCloneTask={canCloneTask ? (task) => setTaskToClone(task) : undefined}
                            onDeleteSeries={
                              canDeleteTaskRow(item.group.activeTask)
                                ? (group) => setSeriesToDelete(group)
                                : undefined
                            }
                            onSnoozeRushHour={
                              settings?.operationalFeatures.roundsRushHourSnoozeEnabled !== false
                                ? async (task) => {
                                    if (!actor) return;
                                    try {
                                      await snoozeTaskForRushHour(gateway, actor, task, 45);
                                      toast.info('تم تأجيل الجولة 45 دقيقة لساعة الذروة بنجاح.');
                                      await load(true);
                                    } catch (err) {
                                      toast.error(err instanceof Error ? err.message : 'تعذّر تأجيل الجولة');
                                    }
                                  }
                                : undefined
                            }
                            onArchiveMissed={async (group) => {
                              if (!actor) return;
                              try {
                                const count = await archiveMissedRoundsInSeries(gateway, actor, group.tasks);
                                toast.success(`تمت تصفية وأرشفة ${count} جولة فائتة سابقة بنجاح ✓`);
                                await load(true);
                              } catch (err) {
                                toast.error(err instanceof Error ? err.message : 'تعذّر تصفية الجولات');
                              }
                            }}
                          />
                        ) : (
                          <TaskCard
                            key={item.key}
                            task={item.task}
                            timeZone={timeZone}
                            onQuickComplete={
                              quickCompleteEnabled
                                ? (task) => setTaskToQuickComplete(task)
                                : undefined
                            }
                            onCloneTask={canCloneTask ? (task) => setTaskToClone(task) : undefined}
                            onDeleteTask={
                              canDeleteTaskRow(item.task)
                                ? (task) => setTaskToDelete(task)
                                : undefined
                            }
                          />
                        ),
                      )}
                      {items.length === 0 ? (
                        <p className="tiny muted center" style={{ padding: '18px 0' }}>
                          لا مهام
                        </p>
                      ) : null}
                      {items.length > 30 ? (
                        <Link to="/tasks/assigned" className="small center">
                          عرض بقية المهام
                        </Link>
                      ) : null}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {!loading && counts && counts.overdue > 0 ? (
        <Card>
          <CardHeader
            title={
              <span className="row danger-text">
                <Icon name="alert" size={18} /> تحتاج انتباهًا
              </span>
            }
            actions={
              <Link to="/tasks/overdue" className="btn btn-sm btn-outline">
                المهام المتأخرة
              </Link>
            }
          />
          <div className="card-body">
            <div className="row wrap">
              {overdueItems.map((item) =>
                  item.kind === 'series' ? (
                    <Link
                      key={item.key}
                      to={`/tasks/${item.group.activeTask.id}`}
                      className="badge badge-danger"
                    >
                      {item.group.baseTitle} ({item.group.tasks.length} جولة)
                    </Link>
                  ) : (
                    <Link key={item.key} to={`/tasks/${item.task.id}`} className="badge badge-danger">
                      {item.task.title}
                    </Link>
                  ),
                )}
            </div>
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        open={taskToDelete !== null}
        title="حذف المهمة نهائيًا"
        message={
          taskToDelete ? (
            <div className="col" style={{ gap: 8 }}>
              <p>
                هل تريد حذف المهمة <strong>«{taskToDelete.title}»</strong> ومرفقاتها وسجلها نهائيًا؟
              </p>
            </div>
          ) : (
            ''
          )
        }
        confirmLabel="حذف المهمة نهائيًا"
        danger
        loading={deleting}
        onClose={() => setTaskToDelete(null)}
        onConfirm={async () => {
          if (!actor || !taskToDelete) return;
          setDeleting(true);
          try {
            await deleteTask(gateway, actor, taskToDelete.id);
            optimisticRemoveTaskFromCache(taskToDelete.id);
            toast.success('تم حذف المهمة نهائيًا.');
            setTaskToDelete(null);
            await refresh();
            await load(true);
          } catch (cause) {
            toast.error(cause instanceof Error ? cause.message : 'تعذّر حذف المهمة');
          } finally {
            setDeleting(false);
          }
        }}
      />

      <ConfirmDialog
        open={seriesToDelete !== null}
        title="حذف جولات السلسلة المتكررة"
        message={
          seriesToDelete ? (
            <div className="col" style={{ gap: 8 }}>
              <p>
                هل تريد حذف جميع الجولات المعروضة لهذه المهمة المتكررة{' '}
                <strong>«{seriesToDelete.baseTitle}»</strong> وعددها{' '}
                <strong>{seriesToDelete.tasks.length} جولة</strong> نهائيًا؟
              </p>
            </div>
          ) : (
            ''
          )
        }
        confirmLabel={`حذف ${seriesToDelete?.tasks.length ?? 0} جولة نهائيًا`}
        danger
        loading={deleting}
        onClose={() => setSeriesToDelete(null)}
        onConfirm={async () => {
          if (!actor || !seriesToDelete) return;
          setDeleting(true);
          try {
            const count = await deleteTasksBatch(
              gateway,
              actor,
              seriesToDelete.tasks.map((t) => t.id),
            );
            seriesToDelete.tasks.forEach((t) => optimisticRemoveTaskFromCache(t.id));
            toast.success(`تم حذف ${count} جولة نهائيًا.`);
            setSeriesToDelete(null);
            await refresh();
            await load(true);
          } catch (cause) {
            toast.error(cause instanceof Error ? cause.message : 'تعذّر حذف الجولات');
          } finally {
            setDeleting(false);
          }
        }}
      />

      <CloneTaskModal
        task={taskToClone}
        gateway={gateway}
        actor={actor}
        branches={lookups.branches}
        members={lookups.members}
        branchNames={lookups.branchNames}
        memberNames={lookups.memberNames}
        onClose={() => setTaskToClone(null)}
        onSuccess={async (created) => {
          toast.success(`تم نسخ المهمة بنجاح (${created.length} مهمة).`);
          setTaskToClone(null);
          await refresh();
          await load();
        }}
      />

      <QuickCompleteTaskModal
        task={taskToQuickComplete}
        gateway={gateway}
        actor={actor}
        branchName={
          taskToQuickComplete?.branchId
            ? (lookups.branchNames.get(taskToQuickComplete.branchId) ?? '')
            : ''
        }
        actorName={snapshot?.membershipProfile?.fullName ?? snapshot?.profile?.fullName ?? 'الموظف'}
        photoWatermarkEnabled={settings?.operationalFeatures.photoWatermarkEnabled !== false}
        cameraOnlyProof={Boolean(settings?.operationalFeatures.cameraOnlyProof)}
        checklistRequired={Boolean(settings?.operationalFeatures.roundsChecklistRequired)}
        onClose={() => setTaskToQuickComplete(null)}
        onSuccess={async () => {
          toast.success('تم إنجاز المهمة بنجاح ✓');
          setTaskToQuickComplete(null);
          await refresh();
          await load();
        }}
      />
    </div>
  );
}
